
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { ColorVariant } from '@/lib/colors';

export type GlassPillVariant = ColorVariant;

interface GlassPillProps {
  type: string;
  variant?: GlassPillVariant;
  className?: string;
  showSpinner?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const variantClasses: Record<GlassPillVariant, string> = {
  default: 'bg-gray-100/80 text-gray-700 border-gray-200/50 dark:bg-gray-800/80 dark:text-gray-300 dark:border-gray-700/50',
  green: 'bg-emerald-100/80 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50',
  blue: 'bg-blue-100/80 text-blue-700 border-blue-200/50 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/50',
  purple: 'bg-purple-100/80 text-purple-700 border-purple-200/50 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700/50',
  red: 'bg-red-100/80 text-red-700 border-red-200/50 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700/50',
  amber: 'bg-amber-100/80 text-amber-700 border-amber-200/50 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50',
  emerald: 'bg-emerald-100/80 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50',
  gray: 'bg-gray-100/80 text-gray-700 border-gray-200/50 dark:bg-gray-800/80 dark:text-gray-300 dark:border-gray-700/50',
  documentation: 'bg-blue-100/80 text-blue-700 border-blue-200/50 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/50',
  video: 'bg-purple-100/80 text-purple-700 border-purple-200/50 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700/50',
  link: 'bg-green-100/80 text-green-700 border-green-200/50 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700/50',
  cyan: 'bg-cyan-100/80 text-cyan-700 border-cyan-200/50 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-700/50',
  rose: 'bg-rose-100/80 text-rose-700 border-rose-200/50 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700/50',
  indigo: 'bg-indigo-100/80 text-indigo-700 border-indigo-200/50 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700/50',
  vibrant: 'bg-violet-100/80 text-violet-700 border-violet-200/50 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700/50',
  destructive: 'bg-red-100/80 text-red-700 border-red-200/50 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700/50',
  fuchsia: 'bg-fuchsia-100/80 text-fuchsia-700 border-fuchsia-200/50 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 dark:border-fuchsia-700/50',
  orange: 'bg-orange-100/80 text-orange-700 border-orange-200/50 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700/50',
  pink: 'bg-pink-100/80 text-pink-700 border-pink-200/50 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-700/50',
  slate: 'bg-slate-100/80 text-slate-700 border-slate-200/50 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-700/50',
  zinc: 'bg-zinc-100/80 text-zinc-700 border-zinc-200/50 dark:bg-zinc-900/30 dark:text-zinc-300 dark:border-zinc-700/50',
  neutral: 'bg-neutral-100/80 text-neutral-700 border-neutral-200/50 dark:bg-neutral-900/30 dark:text-neutral-300 dark:border-neutral-700/50',
  stone: 'bg-stone-100/80 text-stone-700 border-stone-200/50 dark:bg-stone-900/30 dark:text-stone-300 dark:border-stone-700/50',
  teal: 'bg-teal-100/80 text-teal-700 border-teal-200/50 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700/50',
  sky: 'bg-sky-100/80 text-sky-700 border-sky-200/50 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700/50',
  violet: 'bg-violet-100/80 text-violet-700 border-violet-200/50 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700/50',
};

const GlassPill: React.FC<GlassPillProps> = ({ 
  type, 
  variant = 'default', 
  className = '',
  showSpinner = false,
  children,
  icon,
  iconPosition = 'left'
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
        'backdrop-blur-sm border shadow-sm',
        'transition-all duration-200',
        variantClasses[variant],
        className
      )}
    >
      {showSpinner && (
        <Loader2 className="h-3 w-3 animate-spin" />
      )}
      {icon && iconPosition === 'left' && icon}
      {children || type}
      {icon && iconPosition === 'right' && icon}
    </motion.div>
  );
};

export default GlassPill;
